import type { serve } from '@hono/node-server'
import as from 'ansis'
import { Command } from 'commander'

import {
  loadOpenApiFile,
  printSpecificationBanner,
} from '@/domains/document/helpers'
import { watchFile } from '@/helpers'
import { output } from '@/helpers/std-out'

import { bootServer, printAvailablePaths } from './helpers'

export function MockCommand() {
  const cmd = new Command('mock')

  cmd.description('Mock an API from an OpenAPI file')
  cmd.argument('[file|url]', 'OpenAPI file or URL to mock the server for')
  cmd.option('-w, --watch', 'watch the file for changes')
  cmd.option('-o, --once', 'run the server only once and exit after that')
  cmd.option('-p, --port <port>', 'set the HTTP port for the mock server')
  cmd.action(
    async (
      fileArgument: string,
      { watch, once, port }: { watch?: boolean; once?: boolean; port?: number },
    ) => {
      // Server instance
      let server: ReturnType<typeof serve>

      // Load OpenAPI file
      const result = await loadOpenApiFile(fileArgument)

      if (!result.valid) {
        return output
          .error()
          .title('Invalid OpenAPI specification')
          .exit('error')
      }

      printSpecificationBanner({
        version: result.version,
        schema: result.schema,
      })

      let { specification } = result

      // Watch OpenAPI file for changes
      if (watch) {
        await watchFile(fileArgument, async () => {
          const newResult = await loadOpenApiFile(fileArgument)
          const specificationHasChanged =
            newResult?.specification &&
            JSON.stringify(specification) !==
              JSON.stringify(newResult.specification)

          if (specificationHasChanged) {
            output.info().title(as.grey('OpenAPI file modified')).print()

            printSpecificationBanner({
              version: newResult.version,
              schema: newResult.schema,
            })

            specification = newResult.specification

            // Update mock server
            if (specification) {
              server.close()
              server = await bootServer({
                specification: specification,
                port,
              })
            }
          }
        })
      }

      // Show all paths from the specification
      if (!specification) {
        output
          .warn()
          .title('Could not find any paths in the OpenAPI file.')
          .print()
        return
      }

      printAvailablePaths(specification)

      // Listen for requests
      server = await bootServer({
        specification,
        port,
      })

      // Exit after the first run
      if (once) {
        setTimeout(() => {
          server.close()
        }, 2000)
      }
    },
  )

  return cmd
}
